import {Divider, makeStyles, MenuItem, MenuList, Typography, Box} from "@material-ui/core";
import PropTypes from "prop-types";
import React, {useContext, useState} from "react";
import Select from "../../form/controls/select/Select";
import FormControl from "@material-ui/core/FormControl";
import Popover from "@material-ui/core/Popover";
import CountryIcon from "../../country-icon/CountryIcon";
import IconButton from "@material-ui/core/IconButton";
import {LocaleContext} from "../../../../store/providers/LocaleProvider";
import {SUPPORTED_LANGUAGE} from "../../../../common/i18n/locales/localeConstant";
import {LocalizedContext} from "../../../../store/providers/LocalizedProvider";
import StreamlineIcon from "../../streamline-icon/StreamlineIcon";
import Check from "@hapinest/theme/icons/streamlinehq/img/streamline-regular/interface-essential/form-validation/check-1.svg";
import clsx from "clsx";
import {getCachedData, contentful} from "../../../../common/apollo";
import {QUERY as ComposePagesQuery} from "../../../../hooks/useComposePages";
import ArrowRight from "@hapinest/theme/icons/streamlinehq/img/streamline-regular/arrows-diagrams/arrows/arrow-right-1.svg";
import Dialog from "@material-ui/core/Dialog";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import {useI18N} from "../../../../common/common";
import CloseIcon from "@material-ui/icons/Close";
import {useAnalytics} from "../../../../store/providers/AnalyticsProvider";

const useStyles = makeStyles(theme => ({
    select: {
        height: theme.spacing(4),
        background: theme.palette.grey["0"]
    },
    selectPaddingTop: {
        paddingTop: theme.spacing(2.5),
        "&:focus": {
            background: "unset !important"
        }
    },
    flagIcon: {
        height: theme.spacing(2),
        width: theme.spacing(2),
    },
    ml1: {
        marginLeft: theme.spacing(1.2)
    },
    popover: {
        minWidth: "240px",
        borderRadius: theme.spacing(1),
    },
    menuItem: {
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3),
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),
        "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.08)"
        }
    },
    checkmarkIcon: {
        marginLeft: "auto"
    },
    preselectedItem: {
        backgroundColor: "rgba(0, 0, 0, 0.08)"
    },
    arrowIconRight: {
        position: "absolute",
        right: theme.spacing(-1),
        top: theme.spacing(1),
        [theme.breakpoints.down("xs")]: {
            top: theme.spacing(2),
        }
    },
    colorWhite: {
        color: theme.palette.text.white
    },
    closeButton: {
        position: "absolute",
        right: theme.spacing(2),
        top: theme.spacing(0.5),
        color: theme.palette.grey[500],
    }
}));

const LanguageSelect = ({type, formControlClassName, handleSelection, hideDividers, showSelectedIndicator, iconClass, location}) => {
    const [localeState, localeDispatch] = useContext(LocaleContext);
    const {sendCustomEvent, eventNames} = useAnalytics();
    const {locale, localeSelection} = localeState;
    const [localizedState] = useContext(LocalizedContext);
    const [anchorEl, setAnchorEl] = useState(null);
    const classes = useStyles();
    const [open, setOpen] = useState(false);
    const {i18n} = useI18N();

    const handleOpenPopover = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClosePopover = () => {
        setAnchorEl(null);
    };

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleLanguageChange = (name, value) => {
        if (SUPPORTED_LANGUAGE?.hasOwnProperty(value)) {
            if (locale !== SUPPORTED_LANGUAGE[value]) {
                /*
                 *  Check if compose pages are cached, and if yes, pass
                 *  them to the dispatcher in case the current route resolves
                 *  to a compose page
                */
                const cachedComposePages = getCachedData(ComposePagesQuery, contentful);
                localeDispatch({type: "SET_LOCALE", payload: {value, localizedState, composePages: cachedComposePages?.pageCollection?.items}});
                handleGA4LanguageChangeTracking(value);
            }
        }
        if (type === "popover") {
            handleClosePopover();
        }

        if (handleSelection) {
            handleSelection(value);
        }
    };

    const handleGA4LanguageChangeTracking = (value) => {
        sendCustomEvent({
            event_name: eventNames.CHANGE_LOCALE,
            [eventNames.CHANGE_LOCALE]: {
                page_locale: value,
                navigation_type: location
            }
        });
    };

    return (
        <>
            {type === "formControl" &&
                <FormControl
                    className={formControlClassName}
                    variant="outlined"
                >
                    {localeSelection && localeSelection.localeOptions &&
                        <Select
                            name={"selectCountry"}
                            className={classes.select}
                            rootClassName={classes.selectPaddingTop}
                            entries={localeSelection.localeOptions}
                            onChange={handleLanguageChange}
                            value={localeSelection.localeSelected}
                        />
                    }
                </FormControl>
            }

            {type === "popover" &&
                <>
                    <IconButton
                        onClick={handleOpenPopover}
                        e2e-id={"button-select-country"}
                    >
                        <CountryIcon
                            className={iconClass || classes.flagIcon}
                            countryCode={localeSelection.localeSelected === "at_DE" || localeSelection.localeSelected === "ch_DE"
                                ? localeSelection.localeSelected.toUpperCase().split("_")[0]
                                : localeSelection.localeSelected.split("_")[1]
                            }
                        />
                    </IconButton>

                    <Popover
                        open={Boolean(anchorEl)}
                        anchorEl={anchorEl}
                        onClose={handleClosePopover}
                        keepMounted
                        anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "left",
                        }}
                        classes={{paper: classes.popover}}
                    >
                        <MenuList>
                            {localeSelection && localeSelection.localeOptions.map((option, index) => {
                                return (
                                    <div key={index}>
                                        <MenuItem
                                            e2e-id={"menu-item-" + option.value}
                                            className={classes.menuItem}
                                            onClick={() => handleLanguageChange("selectCountry", option.value)}
                                        >
                                            <CountryIcon
                                                className={iconClass || classes.flagIcon}
                                                countryCode={option?.countryCode}
                                            />

                                            <Typography
                                                variant={"body1"}
                                                className={classes.ml1}
                                            >
                                                {option.label}
                                            </Typography>

                                            {showSelectedIndicator && localeSelection?.localeSelected === option.value &&
                                                <StreamlineIcon
                                                    className={classes.checkmarkIcon}
                                                    size={20}
                                                >
                                                    <Check />
                                                </StreamlineIcon>
                                            }
                                        </MenuItem>

                                        {index !== (localeSelection.localeOptions.length - 1) && !hideDividers &&
                                            <Divider />
                                        }
                                    </div>
                                );
                            })}
                        </MenuList>
                    </Popover>
                </>
            }

            {type === "list" &&
                <MenuList>
                    {localeSelection && localeSelection?.localeOptions.map((option, index) => {
                        return (
                            <div key={index}>
                                <MenuItem
                                    e2e-id={"menu-item-" + option.value}
                                    className={clsx(classes.menuItem, {[classes.preselectedItem]: (showSelectedIndicator && localeSelection?.localeSelected === option.value)})}
                                    onClick={() => handleLanguageChange("selectCountry", option.value)}
                                >
                                    <CountryIcon
                                        className={iconClass || classes.flagIcon}
                                        countryCode={option?.countryCode}
                                    />

                                    <Typography
                                        variant={"body1"}
                                        className={classes.ml1}
                                    >
                                        {option.label}
                                    </Typography>

                                    {showSelectedIndicator && localeSelection?.localeSelected === option.value &&
                                        <StreamlineIcon
                                            className={classes.checkmarkIcon}
                                            size={20}
                                        >
                                            <Check />
                                        </StreamlineIcon>
                                    }
                                </MenuItem>

                                {index !== (localeSelection.localeOptions.length - 1) && !hideDividers &&
                                    <Divider />
                                }
                            </div>
                        );
                    })}
                </MenuList>
            }

            {type === "listOverlay" &&
                <>
                    <MenuItem onClick={handleClickOpen}>
                        <CountryIcon
                            className={iconClass || classes.flagIcon}
                            countryCode={localeSelection.localeSelected === "at_DE" || localeSelection.localeSelected === "ch_DE"
                                ? localeSelection.localeSelected.toUpperCase().split("_")[0]
                                : localeSelection.localeSelected.split("_")[1]
                            }
                        />

                        <Typography
                            variant="body1"
                            className={classes.colorWhite}
                        >
                            {localeSelection?.localeOptions.find(o => o.value === localeSelection.localeSelected)?.label}
                        </Typography>

                        <StreamlineIcon
                            className={clsx(classes.arrowIconRight, classes.colorWhite)}
                            size={16}
                        >
                            <ArrowRight />
                        </StreamlineIcon>
                    </MenuItem>
                    <Dialog
                        fullScreen
                        open={open}
                        onClose={handleClose}
                    >
                        <MuiDialogTitle
                            id="customized-dialog-title"
                            onClose={handleClose}
                        >
                            <Box>
                                <Typography
                                    variant="h5"
                                    className={classes.ml1}
                                >
                                    {i18n("view_header.profile_menu.language_selection")}
                                </Typography>
                            </Box>

                            <IconButton
                                aria-label="close"
                                className={classes.closeButton}
                                onClick={handleClose}
                            >
                                <CloseIcon />
                            </IconButton>
                        </MuiDialogTitle>

                        <MenuList>
                            {localeSelection && localeSelection?.localeOptions.map((option, index) => {
                                return (
                                    <div key={index}>
                                        <MenuItem
                                            e2e-id={"menu-item-" + option.value}
                                            className={clsx(classes.menuItem, {[classes.preselectedItem]: (showSelectedIndicator && localeSelection?.localeSelected === option.value)})}
                                            onClick={() => handleLanguageChange("selectCountry", option.value)}
                                        >
                                            <CountryIcon
                                                className={iconClass || classes.flagIcon}
                                                countryCode={option?.countryCode}
                                            />

                                            <Typography
                                                variant={"body1"}
                                                className={classes.ml1}
                                            >
                                                {option.label}
                                            </Typography>

                                            {showSelectedIndicator && localeSelection?.localeSelected === option.value &&
                                                <StreamlineIcon
                                                    className={classes.checkmarkIcon}
                                                    size={20}
                                                >
                                                    <Check />
                                                </StreamlineIcon>
                                            }
                                        </MenuItem>

                                        {index !== (localeSelection.localeOptions.length - 1) && !hideDividers &&
                                            <Divider />
                                        }
                                    </div>
                                );
                            })}
                        </MenuList>
                    </Dialog>
                </>
            }
        </>
    );
};

LanguageSelect.propTypes = {
    type: PropTypes.oneOf(["formControl", "popover", "list", "listOverlay"]),
    formControlClassName: PropTypes.string,
    handleSelection: PropTypes.func,
    hideDividers: PropTypes.bool,
    showSelectedIndicator: PropTypes.bool,
    iconClass: PropTypes.string,
    location: PropTypes.string,
};

export default LanguageSelect;
